import { FileText } from "lucide-react";

// Plain-English Terms of Service. Written to satisfy Apple App Store /
// Google Play submission and to give any learner a clear picture of the deal.
// This is not legal advice — have counsel review before you rely on it.

const LAST_UPDATED = "February 2026";
const CONTACT_EMAIL = "support@liftupreading.com";
const COMPANY = "Lift Up Literacy LLC";

export default function Terms() {
    return (
        <main className="container-page py-16 fade-up" data-testid="terms-page">
            <div className="max-w-3xl mx-auto">
                <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-lg bg-[#E0EDE6] flex items-center justify-center">
                        <FileText className="w-6 h-6 text-[#2D5D4E]" strokeWidth={1.6} />
                    </div>
                    <p className="label-eyebrow">Legal</p>
                </div>
                <h1
                    className="text-4xl sm:text-5xl font-semibold text-[#1C2321] mb-3"
                    style={{ fontFamily: "Outfit" }}
                    data-testid="terms-title"
                >
                    Terms of service
                </h1>
                <p className="text-[#819888] mb-10">
                    Last updated: {LAST_UPDATED}. Written in plain English by a real person.
                </p>

                <section className="card-rw p-7 mb-6 leading-relaxed text-[#1C2321]">
                    <h2 className="text-2xl font-medium mb-3" style={{ fontFamily: "Outfit" }}>
                        Short version
                    </h2>
                    <ul className="space-y-2 list-disc pl-5">
                        <li>The Lift Up Reading app is <span className="font-medium">free</span>. Sign up, learn, stay as long as you want.</li>
                        <li>Be kind to the tools and to yourself. Don't try to break the app or use it to harm others.</li>
                        <li>One-on-one tutoring is a separate paid service run by our sister company. Different rules apply — see below.</li>
                        <li>We do our best but the app is provided "as is" — there's no legal warranty.</li>
                        <li>Questions or a problem? <a className="underline text-[#2D5D4E]" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. A real human reads it.</li>
                    </ul>
                </section>

                <section className="card-rw p-7 mb-6" data-testid="terms-section-agreement">
                    <h2 className="text-2xl font-medium mb-3" style={{ fontFamily: "Outfit" }}>1 · The agreement</h2>
                    <p>
                        By creating an account or using Lift Up Reading (the "app"), you agree to
                        these terms and to our{" "}
                        <a className="underline text-[#2D5D4E]" href="/privacy">privacy policy</a>.
                        If you don't agree, that's OK — please don't use the app. The app is
                        operated by <span className="font-medium">{COMPANY}</span>.
                    </p>
                </section>

                <section className="card-rw p-7 mb-6" data-testid="terms-section-account">
                    <h2 className="text-2xl font-medium mb-3" style={{ fontFamily: "Outfit" }}>2 · Your account</h2>
                    <ul className="space-y-2 list-disc pl-5">
                        <li>You need to be at least 13 to create an account. The app is designed for adult learners; if you're a minor, please use it with a parent or guardian.</li>
                        <li>Give us a real email so we can send you password resets and your weekly recap.</li>
                        <li>Keep your password to yourself. You're responsible for what happens on your account.</li>
                        <li>Tell us at <a className="underline text-[#2D5D4E]" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> right away if you think someone's accessing it without permission.</li>
                    </ul>
                </section>

                <section className="card-rw p-7 mb-6" data-testid="terms-section-free">
                    <h2 className="text-2xl font-medium mb-3" style={{ fontFamily: "Outfit" }}>3 · The app is free</h2>
                    <p className="mb-3">
                        Every lesson, game, AI tutor conversation, read-aloud check, and email
                        recap is included at no charge. There's no premium tier and no paywalled
                        content. We reserve the right to change this in the future, but if we ever
                        introduce paid features we'll tell you clearly and never charge without
                        your explicit consent.
                    </p>
                    <p>
                        In exchange for the app being free, we ask that you use it as intended —
                        for your own reading practice or that of a learner you're helping.
                    </p>
                </section>

                <section className="card-rw p-7 mb-6" data-testid="terms-section-tutoring">
                    <h2 className="text-2xl font-medium mb-3" style={{ fontFamily: "Outfit" }}>4 · One-on-one tutoring (separate, paid)</h2>
                    <p className="mb-3">
                        If you separately purchase virtual tutoring sessions from{" "}
                        <span className="font-medium">Lift Up Literacy LLC</span>, the following
                        apply:
                    </p>
                    <ul className="space-y-2 list-disc pl-5">
                        <li>Payments are processed by Stripe. We don't store your full card details.</li>
                        <li>You'll receive a confirmation email with your session date and joining link.</li>
                        <li>To reschedule, please give at least 24 hours' notice. See our{" "}
                            <a className="underline text-[#2D5D4E]" href="/refund">refund policy</a>{" "}
                            for the specifics on cancellations and refunds.
                        </li>
                        <li>Missed sessions with less than 24 hours' notice are non-refundable.</li>
                    </ul>
                </section>

                <section className="card-rw p-7 mb-6" data-testid="terms-section-acceptable-use">
                    <h2 className="text-2xl font-medium mb-3" style={{ fontFamily: "Outfit" }}>5 · Acceptable use</h2>
                    <p className="mb-3">
                        Please don't do any of the following with the app:
                    </p>
                    <ul className="space-y-2 list-disc pl-5">
                        <li>Try to reverse-engineer, scrape, or overload the service.</li>
                        <li>Use it to harass, threaten, or dox another person.</li>
                        <li>Send the AI tutor content that's illegal, sexually explicit, or targets a minor.</li>
                        <li>Impersonate someone else or pretend to represent us.</li>
                        <li>Sell, sub-license, or repackage access to the app.</li>
                    </ul>
                    <p className="mt-3">
                        We may suspend or delete accounts that break these rules. We'll usually
                        reach out first if we can.
                    </p>
                </section>

                <section className="card-rw p-7 mb-6" data-testid="terms-section-content">
                    <h2 className="text-2xl font-medium mb-3" style={{ fontFamily: "Outfit" }}>6 · Our content, your content</h2>
                    <p className="mb-3">
                        The lessons, passages, games, tutor persona, and design are our
                        intellectual property. You get a personal, non-transferable licence to use
                        them for your own learning. Please don't republish or copy them elsewhere.
                    </p>
                    <p>
                        Anything <em>you</em> type or record (tutor messages, read-aloud audio,
                        journal entries) stays yours. You give us the minimum licence needed to
                        run the app for you: transcribe your read-aloud audio, generate a tutor
                        reply, save your progress. See{" "}
                        <a className="underline text-[#2D5D4E]" href="/privacy">privacy</a>{" "}
                        for who else touches it.
                    </p>
                </section>

                <section className="card-rw p-7 mb-6" data-testid="terms-section-ai">
                    <h2 className="text-2xl font-medium mb-3" style={{ fontFamily: "Outfit" }}>7 · A note on the AI tutor</h2>
                    <p>
                        The AI tutor is a language model. It's patient and usually accurate, but
                        it can be wrong. Treat its answers as a helpful second opinion — not
                        medical, legal, or financial advice. If something feels off, ask a
                        human.
                    </p>
                </section>

                <section className="card-rw p-7 mb-6" data-testid="terms-section-warranty">
                    <h2 className="text-2xl font-medium mb-3" style={{ fontFamily: "Outfit" }}>8 · "As is" — no warranty</h2>
                    <p>
                        We work hard to keep the app running well, but we can't promise it'll be
                        uninterrupted, bug-free, or perfectly accurate all the time. The app is
                        provided <span className="font-medium">"as is"</span> and{" "}
                        <span className="font-medium">"as available"</span>, without warranties
                        of any kind, express or implied.
                    </p>
                </section>

                <section className="card-rw p-7 mb-6" data-testid="terms-section-liability">
                    <h2 className="text-2xl font-medium mb-3" style={{ fontFamily: "Outfit" }}>9 · Limits of liability</h2>
                    <p className="mb-3">
                        To the maximum extent allowed by law, {COMPANY}, its affiliates, and its
                        employees are not liable for any indirect, incidental, consequential, or
                        punitive damages arising out of your use of the app — including lost data
                        or profits.
                    </p>
                    <p>
                        Our total liability to you, for anything related to the free app, is
                        capped at <span className="font-medium">USD $50</span>. Where you've paid
                        us for tutoring, it's capped at what you paid us in the 12 months before
                        the claim.
                    </p>
                </section>

                <section className="card-rw p-7 mb-6" data-testid="terms-section-termination">
                    <h2 className="text-2xl font-medium mb-3" style={{ fontFamily: "Outfit" }}>10 · Ending things</h2>
                    <p>
                        You can delete your account at any time (see{" "}
                        <a className="underline text-[#2D5D4E]" href="/privacy">privacy</a>). We
                        may end your access if you materially break these terms — we'll try to
                        give you a chance to fix it first when we can.
                    </p>
                </section>

                <section className="card-rw p-7 mb-6" data-testid="terms-section-changes">
                    <h2 className="text-2xl font-medium mb-3" style={{ fontFamily: "Outfit" }}>11 · Changes to these terms</h2>
                    <p>
                        If we make a meaningful change, we'll update the "last updated" date and
                        email everyone with an account before it takes effect. Small clarifying
                        edits — typos, links — might land quietly.
                    </p>
                </section>

                <section className="card-rw p-7 mb-6" data-testid="terms-section-law">
                    <h2 className="text-2xl font-medium mb-3" style={{ fontFamily: "Outfit" }}>12 · Governing law</h2>
                    <p>
                        These terms are governed by the laws of the United States and the state
                        in which {COMPANY} is registered, without regard to conflict-of-law
                        principles. If a dispute arises, we agree to try to resolve it in good
                        faith before going to court.
                    </p>
                </section>

                <section className="card-rw p-7" data-testid="terms-section-contact">
                    <h2 className="text-2xl font-medium mb-3" style={{ fontFamily: "Outfit" }}>13 · Contact</h2>
                    <p>
                        Questions, concerns, or a report of misuse — email{" "}
                        <a
                            className="underline text-[#2D5D4E] font-medium"
                            href={`mailto:${CONTACT_EMAIL}`}
                            data-testid="terms-contact-email"
                        >
                            {CONTACT_EMAIL}
                        </a>
                        .
                    </p>
                </section>
            </div>
        </main>
    );
}
